import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from '../utils/s3Upload'
import { API_URL } from '../config/api'

const MAX_ATTACHMENTS = 10

function parsePriceApiResponse(raw) {
  let responseData = raw
  if (typeof responseData === 'string') {
    try {
      responseData = JSON.parse(responseData)
    } catch {
      responseData = raw
    }
  }
  if (responseData?.body != null) {
    const b = responseData.body
    if (typeof b === 'string') {
      try {
        responseData = JSON.parse(b)
      } catch {
        responseData = b
      }
    } else {
      responseData = b
    }
  }
  if (responseData?.data != null && typeof responseData.data === 'string') {
    try {
      responseData = {
        ...responseData,
        data: JSON.parse(responseData.data)
      }
    } catch {
      // leave as-is
    }
  }
  return responseData
}

function isPriceApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function getPresignedUploadUrl(meta) {
  return String(
    meta?.uploadUrl ||
      meta?.url ||
      meta?.presignedUrl ||
      meta?.signedUrl ||
      ''
  ).trim()
}

function normalizePresignFileMetas(responseData, files, contentTypesForPresign) {
  if (!isPriceApiSuccess(responseData)) return null

  const dataBlock =
    responseData?.data && typeof responseData.data === 'object'
      ? responseData.data
      : responseData
  const nestedBlock =
    dataBlock?.data && typeof dataBlock.data === 'object' ? dataBlock.data : null

  const fallbackType = (idx) =>
    contentTypesForPresign?.[idx] ||
    resolveFileContentType(files[idx]) ||
    'application/octet-stream'

  const mapMeta = (meta, idx) => ({
    uploadUrl: getPresignedUploadUrl(meta),
    s3Key: meta?.s3Key || meta?.key,
    originalFileName:
      meta?.originalFileName || meta?.fileName || files[idx]?.name,
    contentType: meta?.contentType || fallbackType(idx)
  })

  for (const list of [dataBlock?.files, responseData?.files, nestedBlock?.files]) {
    if (Array.isArray(list) && list.length > 0) {
      return list.map((meta, idx) => mapMeta(meta, idx))
    }
  }

  for (const list of [
    dataBlock?.uploadUrls,
    nestedBlock?.uploadUrls,
    responseData?.uploadUrls
  ]) {
    if (Array.isArray(list) && list.length > 0) {
      const source = dataBlock?.uploadUrls
        ? dataBlock
        : nestedBlock || responseData
      return list.map((uploadUrl, idx) => ({
        uploadUrl,
        s3Key: source?.s3Keys?.[idx],
        originalFileName: source?.originalFileNames?.[idx] || files[idx]?.name,
        contentType: source?.contentTypes?.[idx] || fallbackType(idx)
      }))
    }
  }

  const single =
    (getPresignedUploadUrl(dataBlock) && dataBlock?.s3Key && dataBlock) ||
    (getPresignedUploadUrl(nestedBlock) && nestedBlock?.s3Key && nestedBlock) ||
    (getPresignedUploadUrl(responseData) && responseData?.s3Key && responseData)

  if (single && files.length === 1) {
    return [mapMeta(single, 0)]
  }

  return null
}

function dateInputToIsoStart(dateStr) {
  if (!dateStr) return ''
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString()
}

function dateInputToIsoEnd(dateStr) {
  if (!dateStr) return ''
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString()
}

function isProductImageDownloadEntry(f, productImageS3Key) {
  if (!f || typeof f !== 'object') return false
  if (f.kind === 'productImage') return true
  const key = f.s3Key != null ? String(f.s3Key) : ''
  if (key.startsWith('Product_attachment/')) return true
  if (productImageS3Key && key === productImageS3Key) return true
  return false
}

const PriceTrackerEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [isLoadingData, setIsLoadingData] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [canEdit, setCanEdit] = useState(true)
  const [permissionMessage, setPermissionMessage] = useState('')

  const [suppliers, setSuppliers] = useState([])
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1)
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(true)
  const [selectedSuppliers, setSelectedSuppliers] = useState([])

  const [formData, setFormData] = useState({
    productName: '',
    purchaseUom: '',
    purchasePricePerUnit: '',
    salesUom: '',
    salesPricePerUnit: '',
    priceValidFrom: '',
    priceValidTill: '',
    remarks: '',
    status: 'active'
  })

  const [existingDownloadFiles, setExistingDownloadFiles] = useState([])
  const [initialProductImage, setInitialProductImage] = useState(null)
  const [productImageRemoved, setProductImageRemoved] = useState(false)
  const [productImageFile, setProductImageFile] = useState(null)
  const [attachmentsToRemove, setAttachmentsToRemove] = useState({})
  const [replaceFilesByS3Key, setReplaceFilesByS3Key] = useState({})
  const [addedFiles, setAddedFiles] = useState([])
  const [isLoadingDownloads, setIsLoadingDownloads] = useState(false)

  const productImageNewPreviewUrl = useMemo(() => {
    if (!productImageFile) return null
    return URL.createObjectURL(productImageFile)
  }, [productImageFile])

  useEffect(() => {
    return () => {
      if (productImageNewPreviewUrl) URL.revokeObjectURL(productImageNewPreviewUrl)
    }
  }, [productImageNewPreviewUrl])

  const addedFilesLabel = useMemo(() => {
    if (addedFiles.length === 0) return 'No files selected'
    return `${addedFiles.length} file${addedFiles.length > 1 ? 's' : ''} selected`
  }, [addedFiles.length])

  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsSuppliersLoading(true)
      try {
        const payload = { action: 'get-active-suppliers' }
        const response = await axios.post(API_URL, payload)

        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (e) {
            responseData = response.data
          }
        }
        if (responseData && responseData.body) {
          if (typeof responseData.body === 'string') {
            try {
              responseData = JSON.parse(responseData.body)
            } catch (e) {
              responseData = responseData.body
            }
          } else {
            responseData = responseData.body
          }
        }

        if (responseData?.success === true && Array.isArray(responseData.data)) {
          setSuppliers(responseData.data)
          setFilteredSuppliers(responseData.data)
        }
      } catch (error) {
        console.error('Fetch suppliers error:', error)
      } finally {
        setIsSuppliersLoading(false)
      }
    }

    fetchSuppliers()
  }, [])

  useEffect(() => {
    const fetchPrice = async () => {
      if (!id) {
        toast.error('Price ID is missing', { autoClose: 2000 })
        navigate('/priceTracker')
        return
      }

      setIsLoadingData(true)
      try {
        const payload = {
          action: 'get-price-by-id',
          id,
          includeDownloadUrls: true
        }

        const response = await axios.post(API_URL, payload)
        const responseData = parsePriceApiResponse(response.data)
        const price =
          responseData?.success === true
            ? responseData.price || responseData.data?.price
            : null

        if (price && typeof price === 'object') {
          const creatorId = price?.createdBy?._id || price?.createdBy || ''

          const currentUserId = localStorage.getItem('id') || ''
          const currentRole = (localStorage.getItem('role') || '').toLowerCase()
          const allowed =
            currentRole === 'admin' ||
            (currentUserId && creatorId && currentUserId === creatorId)
          setCanEdit(Boolean(allowed))
          setPermissionMessage(
            allowed
              ? ''
              : 'Access denied. Only admin users or the user who created this price can update it.'
          )

          const supplierIdsRaw = Array.isArray(price.supplierId)
            ? price.supplierId
            : price.supplierId
              ? [price.supplierId]
              : []
          const supplierNamesRaw = Array.isArray(price.supplierName)
            ? price.supplierName
            : price.supplierName
              ? [price.supplierName]
              : []

          const selected = supplierIdsRaw.map((sid, idx) => ({
            _id: sid,
            clientname: supplierNamesRaw[idx] || supplierNamesRaw[0] || String(sid)
          }))
          setSelectedSuppliers(selected)

          const piS3 =
            price.productImage &&
            typeof price.productImage === 'object' &&
            price.productImage.s3Key
              ? String(price.productImage.s3Key)
              : ''
          const allDownload = Array.isArray(price.downloadFiles) ? price.downloadFiles : []
          const productImgEntry = allDownload.find((f) =>
            isProductImageDownloadEntry(f, piS3)
          )

          setFormData({
            productName: price.productName || '',
            purchaseUom: (price.purchaseUom ?? price.uom ?? '').toString(),
            purchasePricePerUnit:
              price.purchasePricePerUnit != null
                ? String(price.purchasePricePerUnit)
                : price.pricePerUnit != null
                  ? String(price.pricePerUnit)
                  : '',
            salesUom: price.salesUom != null ? String(price.salesUom) : '',
            salesPricePerUnit:
              price.salesPricePerUnit != null ? String(price.salesPricePerUnit) : '',
            priceValidFrom: price.priceValidFrom
              ? new Date(price.priceValidFrom).toISOString().slice(0, 10)
              : '',
            priceValidTill: price.priceValidTill
              ? new Date(price.priceValidTill).toISOString().slice(0, 10)
              : '',
            remarks: price.remarks != null ? String(price.remarks) : '',
            status: price.status || 'active'
          })
          setSupplierSearchTerm('')

          setExistingDownloadFiles(
            allDownload.filter((f) => !isProductImageDownloadEntry(f, piS3))
          )

          if (piS3) {
            setInitialProductImage({
              s3Key: piS3,
              originalFileName: price.productImage?.originalFileName,
              contentType: price.productImage?.contentType,
              downloadUrl: productImgEntry?.downloadUrl || null
            })
          } else {
            setInitialProductImage(null)
          }
          setProductImageRemoved(false)
          setProductImageFile(null)

          // reset attachment edit state on load
          setAttachmentsToRemove({})
          setReplaceFilesByS3Key({})
          setAddedFiles([])
        } else {
          toast.error(responseData?.message || 'Failed to fetch price details', {
            autoClose: 2000
          })
          navigate('/priceTracker')
        }
      } catch (error) {
        console.error('Fetch price error:', error)
        toast.error('Failed to load price details', { autoClose: 2000 })
        navigate('/priceTracker')
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchPrice()
  }, [id, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => {
      const next = { ...prev, [name]: value }

      if (name === 'priceValidFrom') {
        if (next.priceValidTill && value && next.priceValidTill < value) {
          next.priceValidTill = ''
        }
      }

      if (name === 'priceValidTill') {
        if (next.priceValidFrom && value && value < next.priceValidFrom) {
          next.priceValidTill = ''
          toast.error('Valid Till cannot be before Valid From', { autoClose: 2000 })
        }
      }

      return next
    })
  }

  const handleProductImageInputChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file', { autoClose: 2000 })
      return
    }
    setProductImageFile(file)
    setProductImageRemoved(false)
  }

  const clearNewProductImage = () => {
    setProductImageFile(null)
  }

  const handleSupplierSearchChange = (e) => {
    const searchValue = e.target.value
    setSupplierSearchTerm(searchValue)
    setShowSupplierDropdown(true)
    setSelectedSupplierIndex(-1)

    if (searchValue.trim() === '') {
      setFilteredSuppliers(suppliers)
    } else {
      const filtered = suppliers.filter((supplier) =>
        String(supplier.clientname || '')
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
      setFilteredSuppliers(filtered)
    }
  }

  const handleSupplierSelect = (supplier) => {
    const supplierId = supplier._id || ''
    const supplierName = supplier.clientname || ''
    if (!supplierId) return

    setSupplierSearchTerm('')
    setShowSupplierDropdown(false)
    setSelectedSupplierIndex(-1)

    setSelectedSuppliers((prev) => {
      if (prev.some((s) => s._id === supplierId)) return prev
      return [...prev, { _id: supplierId, clientname: supplierName }]
    })
  }

  const handleSupplierKeyDown = (e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedSupplierIndex((prev) =>
          prev < filteredSuppliers.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedSupplierIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (
          selectedSupplierIndex >= 0 &&
          selectedSupplierIndex < filteredSuppliers.length
        ) {
          handleSupplierSelect(filteredSuppliers[selectedSupplierIndex])
        } else if (filteredSuppliers.length === 1) {
          handleSupplierSelect(filteredSuppliers[0])
        }
        break
      case 'Escape':
        setShowSupplierDropdown(false)
        setSelectedSupplierIndex(-1)
        break
      default:
        break
    }
  }

  const handleNewAttachmentsChange = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    setAddedFiles((prev) => {
      const next = [...prev, ...files]
      if (next.length > MAX_ATTACHMENTS) {
        toast.error(`Max ${MAX_ATTACHMENTS} attachments allowed`, { autoClose: 2000 })
        return prev
      }
      return next
    })
    e.target.value = ''
  }

  const loadDownloadLinks = async () => {
    if (!id) return
    setIsLoadingDownloads(true)
    try {
      const payload = { action: 'get-price-download-presigned-url', id }
      const response = await axios.post(API_URL, payload)
      const responseData = parsePriceApiResponse(response.data)

      if (responseData?.success === true && Array.isArray(responseData.files)) {
        const piS3 = initialProductImage?.s3Key || ''
        setExistingDownloadFiles(
          responseData.files.filter((f) => !isProductImageDownloadEntry(f, piS3))
        )
        // keep any remove/replace decisions if user already set them
      } else {
        toast.error(responseData?.message || 'Failed to load download links', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Download link error:', error)
      toast.error('Failed to load download links', { autoClose: 2000 })
    } finally {
      setIsLoadingDownloads(false)
    }
  }

  const uploadFiles = async (filesToUpload, currentUserId) => {
    if (!filesToUpload || filesToUpload.length === 0) return []

    const supplierIdForPrefix = selectedSuppliers[0]?._id
    const contentTypesForPresign = filesToUpload.map((f) =>
      resolveFileContentType(f)
    )

    const payload = {
      action: 'get-price-upload-presigned-url',
      fileNames: filesToUpload.map((f) => f.name),
      contentTypes: contentTypesForPresign,
      files: filesToUpload.map((f, idx) => ({
        fileName: f.name,
        contentType: contentTypesForPresign[idx]
      })),
      supplierId: supplierIdForPrefix || undefined,
      createdBy: currentUserId
    }

    const response = await axios.post(API_URL, payload)
    const responseData = parsePriceApiResponse(response.data)
    const fileMetas = normalizePresignFileMetas(
      responseData,
      filesToUpload,
      contentTypesForPresign
    )

    if (!fileMetas || fileMetas.length !== filesToUpload.length) {
      toast.error(responseData?.message || 'Failed to get upload URLs', {
        autoClose: 2000
      })
      return null
    }

    // S3 PUT via raw fetch — never shared API axios (currentUserId ~44B bug).
    try {
      for (let idx = 0; idx < filesToUpload.length; idx += 1) {
        const fileMeta = fileMetas[idx]
        const file = filesToUpload[idx]
        const contentType =
          fileMeta.contentType ||
          contentTypesForPresign[idx] ||
          'application/octet-stream'
        await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType)
      }
    } catch (error) {
      console.error('Upload price attachments error:', error)
      toast.error(error?.message || 'Failed to upload attachments', {
        autoClose: 2500
      })
      return null
    }

    return fileMetas.map((f, idx) => ({
      s3Key: f.s3Key,
      originalFileName: f.originalFileName || f.fileName || filesToUpload[idx].name,
      contentType:
        f.contentType || contentTypesForPresign[idx] || 'application/octet-stream'
    }))
  }

  const presignAndUploadProductImage = async (file, currentUserId) => {
    if (!file) return null

    if (selectedSuppliers.length === 0) {
      toast.error('Please select a supplier before uploading product image', {
        autoClose: 2000
      })
      return null
    }

    const contentTypeForPresign = resolveFileContentType(file)
    const supplierIdForPrefix = selectedSuppliers[0]?._id
    const payload = {
      action: 'get-price-product-image-upload-presigned-url',
      fileNames: [file.name],
      contentTypes: [contentTypeForPresign],
      supplierId: supplierIdForPrefix,
      createdBy: currentUserId
    }

    const response = await axios.post(API_URL, payload)
    const responseData = parsePriceApiResponse(response.data)
    const fileMetas = normalizePresignFileMetas(
      responseData,
      [file],
      [contentTypeForPresign]
    )

    if (!fileMetas || fileMetas.length !== 1) {
      toast.error(
        responseData?.message || 'Failed to get product image upload URL',
        { autoClose: 2000 }
      )
      return null
    }

    const fileMeta = fileMetas[0]
    const contentType =
      fileMeta.contentType || contentTypeForPresign || 'application/octet-stream'

    try {
      await putFileToPresignedUrl(fileMeta.uploadUrl, file, contentType)
    } catch (error) {
      console.error('Upload product image error:', error)
      toast.error(error?.message || 'Failed to upload product image', {
        autoClose: 2500
      })
      return null
    }

    return {
      s3Key: fileMeta.s3Key,
      originalFileName: fileMeta.originalFileName || fileMeta.fileName || file.name,
      contentType: fileMeta.contentType || contentType
    }
  }

  const validateForm = () => {
    if (selectedSuppliers.length === 0) {
      toast.error('Supplier is required', { autoClose: 2000 })
      return false
    }
    if (!formData.productName.trim()) {
      toast.error('Product name is required', { autoClose: 2000 })
      return false
    }
    if (!formData.purchaseUom.trim()) {
      toast.error('Purchase UOM is required', { autoClose: 2000 })
      return false
    }
    const purchasePrice = parseFloat(formData.purchasePricePerUnit)
    if (
      formData.purchasePricePerUnit === '' ||
      Number.isNaN(purchasePrice) ||
      purchasePrice < 0
    ) {
      toast.error('Valid purchase price per unit is required', { autoClose: 2000 })
      return false
    }
    const salesPriceRaw = String(formData.salesPricePerUnit || '').trim()
    if (salesPriceRaw !== '') {
      const salesPrice = parseFloat(salesPriceRaw)
      if (Number.isNaN(salesPrice) || salesPrice < 0) {
        toast.error('Sales price per unit must be a valid non-negative number when provided', {
          autoClose: 2000
        })
        return false
      }
    }
    if (!formData.priceValidFrom) {
      toast.error('Valid from date is required', { autoClose: 2000 })
      return false
    }
    if (!formData.priceValidTill) {
      toast.error('Valid till date is required', { autoClose: 2000 })
      return false
    }

    const from = new Date(formData.priceValidFrom)
    const till = new Date(formData.priceValidTill)
    if (Number.isNaN(from.getTime()) || Number.isNaN(till.getTime())) {
      toast.error('Please provide valid dates', { autoClose: 2000 })
      return false
    }
    if (from.getTime() > till.getTime()) {
      toast.error('Valid till must be after valid from', { autoClose: 2000 })
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    if (!canEdit) {
      toast.error(
        permissionMessage ||
          'Access denied. Only admin users or the user who created this price can update it.',
        { autoClose: 2500 }
      )
      return
    }

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => navigate('/'), 2000)
      return
    }

    setIsSubmitting(true)
    try {
      const supplierIds = selectedSuppliers.map((s) => s._id)
      const supplierNames = selectedSuppliers.map((s) => s.clientname)

      // Build final attachments list: keep + replace + add
      const currentFiles = Array.isArray(existingDownloadFiles) ? existingDownloadFiles : []
      const keepOrder = currentFiles.filter((f) => f && f.s3Key)

      const replacementEntries = Object.entries(replaceFilesByS3Key) // [oldS3Key, File]
      const replacementFiles = replacementEntries.map(([, file]) => file)
      const filesToUpload = [...replacementFiles, ...addedFiles]

      // Validate max 10 attachments final
      const keptCount = keepOrder.filter(
        (f) => !attachmentsToRemove[f.s3Key] && !replaceFilesByS3Key[f.s3Key]
      ).length
      const finalCount = keptCount + replacementEntries.length + addedFiles.length
      if (finalCount > MAX_ATTACHMENTS) {
        toast.error(`Max ${MAX_ATTACHMENTS} attachments allowed`, { autoClose: 2000 })
        return
      }

      let uploadedProductImageMeta = null
      if (productImageFile) {
        uploadedProductImageMeta = await presignAndUploadProductImage(
          productImageFile,
          currentUserId
        )
        if (uploadedProductImageMeta === null) return
      }

      let uploadedMetas = []
      if (filesToUpload.length > 0) {
        uploadedMetas = await uploadFiles(filesToUpload, currentUserId)
        if (uploadedMetas === null) return
      }

      const replacementMetas = uploadedMetas.slice(0, replacementEntries.length)
      const addedMetas = uploadedMetas.slice(replacementEntries.length)

      const replacementMetaByOldKey = {}
      replacementEntries.forEach(([oldKey], idx) => {
        replacementMetaByOldKey[oldKey] = replacementMetas[idx]
      })

      const finalAttachments = []
      keepOrder.forEach((f) => {
        if (attachmentsToRemove[f.s3Key]) return
        if (replacementMetaByOldKey[f.s3Key]) {
          finalAttachments.push(replacementMetaByOldKey[f.s3Key])
          return
        }
        finalAttachments.push({
          s3Key: f.s3Key,
          originalFileName: f.originalFileName,
          contentType: f.contentType
        })
      })
      addedMetas.forEach((m) => finalAttachments.push(m))

      const purchasePrice = parseFloat(formData.purchasePricePerUnit)
      const salesUomTrim = formData.salesUom.trim()
      const salesPriceRaw = String(formData.salesPricePerUnit || '').trim()
      const salesPrice =
        salesPriceRaw === '' ? null : parseFloat(salesPriceRaw)

      const payload = {
        action: 'update-price',
        currentUserId: localStorage.getItem('id'),
        id,
        productName: formData.productName.trim(),
        purchaseUom: formData.purchaseUom.trim(),
        purchasePricePerUnit: purchasePrice,
        salesUom: salesUomTrim,
        salesPricePerUnit: salesPrice,
        priceValidFrom: dateInputToIsoStart(formData.priceValidFrom),
        priceValidTill: dateInputToIsoEnd(formData.priceValidTill),
        remarks: formData.remarks.trim(),
        status: formData.status,
        supplierId: supplierIds.length === 1 ? supplierIds[0] : supplierIds,
        supplierName: supplierNames.length === 1 ? supplierNames[0] : supplierNames,
        updatedBy: currentUserId
      }

      if (uploadedProductImageMeta) {
        payload.productImage = uploadedProductImageMeta
      } else if (productImageRemoved && initialProductImage) {
        payload.productImage = null
      }

      // Only send attachments if user changed them (remove/replace/add)
      const hasAttachmentChanges =
        Object.keys(attachmentsToRemove).some((k) => attachmentsToRemove[k]) ||
        Object.keys(replaceFilesByS3Key).length > 0 ||
        addedFiles.length > 0
      if (hasAttachmentChanges) {
        payload.attachments = finalAttachments
      }

      const response = await axios.post(API_URL, payload)
      const responseData = parsePriceApiResponse(response.data)

      if (responseData?.success === true) {
        toast.success(responseData.message || 'Price updated successfully', {
          autoClose: 2000
        })
        setTimeout(() => navigate('/priceTracker'), 2000)
      } else {
        if (
          typeof responseData?.message === 'string' &&
          responseData.message.toLowerCase().includes('access denied')
        ) {
          setCanEdit(false)
          setPermissionMessage(responseData.message)
        }
        toast.error(responseData?.message || 'Failed to update price', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Update price error:', error)
      toast.error('An error occurred while updating price', { autoClose: 2000 })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoadingData) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Price</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/priceTracker">
                      <i className="fa-solid fa-arrow-left-long" /> Back
                    </Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Price</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/priceTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      {!canEdit && (
                        <div className="alert alert-warning" role="alert">
                          {permissionMessage ||
                            'You do not have permission to update this price.'}
                        </div>
                      )}
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Supplier Name
                        </label>
                        <div className="col-12 col-md-8">
                          {selectedSuppliers.length > 0 && (
                            <div className="mb-2">
                              {selectedSuppliers.map((s) => (
                                <span
                                  key={s._id}
                                  className="badge badge-info mr-2 mb-1"
                                  style={{ fontSize: '12px', fontWeight: 550 }}
                                >
                                  {s.clientname}
                                  <span
                                    className="ml-2 crsr_pntr"
                                    onMouseDown={(ev) => {
                                      ev.preventDefault()
                                      setSelectedSuppliers((prev) =>
                                        prev.filter((x) => x._id !== s._id)
                                      )
                                    }}
                                    title="Remove"
                                  >
                                    <i className="fa-solid fa-xmark"></i>
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={
                                isSuppliersLoading
                                  ? 'Loading suppliers...'
                                  : 'Type to search supplier and press Enter...'
                              }
                              value={supplierSearchTerm}
                              onChange={handleSupplierSearchChange}
                              onKeyDown={handleSupplierKeyDown}
                              onFocus={() => setShowSupplierDropdown(true)}
                              onBlur={() => {
                                setTimeout(() => {
                                  setShowSupplierDropdown(false)
                                  setSelectedSupplierIndex(-1)
                                }, 200)
                              }}
                              autoComplete="off"
                              disabled={isSuppliersLoading || !canEdit}
                            />
                            {showSupplierDropdown &&
                              !isSuppliersLoading &&
                              filteredSuppliers.length > 0 && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    backgroundColor: '#fff',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    zIndex: 1000,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  {filteredSuppliers.map((supplier, index) => (
                                    <div
                                      key={supplier._id}
                                      onMouseDown={(ev) => {
                                        ev.preventDefault()
                                        handleSupplierSelect(supplier)
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #f0f0f0',
                                        backgroundColor:
                                          index === selectedSupplierIndex
                                            ? '#e9ecef'
                                            : '#fff'
                                      }}
                                      onMouseEnter={() => setSelectedSupplierIndex(index)}
                                    >
                                      {supplier.clientname}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                          <small className="form-text text-muted">
                            You can select multiple suppliers. Backend will sync names to Supplier master.
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Product Name
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="productName"
                            className="form-control"
                            value={formData.productName}
                            onChange={handleChange}
                            disabled={!canEdit}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase UOM
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="purchaseUom"
                            className="form-control"
                            value={formData.purchaseUom}
                            onChange={handleChange}
                            disabled={!canEdit}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Purchase price per unit
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="purchasePricePerUnit"
                            className="form-control"
                            value={formData.purchasePricePerUnit}
                            onChange={handleChange}
                            disabled={!canEdit}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales UOM
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="salesUom"
                            className="form-control"
                            placeholder="e.g. NOS (optional)"
                            value={formData.salesUom}
                            onChange={handleChange}
                            disabled={!canEdit}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Sales price per unit
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="salesPricePerUnit"
                            className="form-control"
                            placeholder="0 (optional)"
                            value={formData.salesPricePerUnit}
                            onChange={handleChange}
                            disabled={!canEdit}
                          />
                        </div>
                      </div>

                      <div className="form-group row align-items-start">
                        <label className="col-12 col-md-4 col-form-label">
                          Product image
                        </label>
                        <div className="col-12 col-md-8">
                          {productImageFile ? (
                            <div>
                              <div
                                className="mb-2"
                                style={{
                                  width: '100%',
                                  maxWidth: '320px',
                                  height: '200px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '6px',
                                  backgroundColor: '#f8f9fa',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <img
                                  src={productImageNewPreviewUrl || ''}
                                  alt=""
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              </div>
                              <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                                <span className="text-muted" style={{ fontSize: '13px' }}>
                                  {productImageFile.name}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  disabled={!canEdit}
                                  onClick={clearNewProductImage}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : !productImageRemoved && initialProductImage ? (
                            <div>
                              {initialProductImage.downloadUrl &&
                              String(
                                initialProductImage.contentType || ''
                              )
                                .toLowerCase()
                                .startsWith('image/') ? (
                                <div
                                  className="mb-2"
                                  style={{
                                    width: '100%',
                                    maxWidth: '320px',
                                    height: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    boxSizing: 'border-box'
                                  }}
                                >
                                  <a
                                    href={initialProductImage.downloadUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Open image"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '100%',
                                      height: '100%',
                                      padding: '8px',
                                      boxSizing: 'border-box'
                                    }}
                                  >
                                    <img
                                      src={initialProductImage.downloadUrl}
                                      alt=""
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  </a>
                                </div>
                              ) : (
                                <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                                  Image is saved; use Replace to change or refresh links to
                                  preview.
                                </p>
                              )}
                              <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                                <label
                                  className="btn btn-outline-primary btn-sm mb-0"
                                  htmlFor="priceEditProductImageReplace"
                                  style={{ cursor: canEdit ? 'pointer' : 'not-allowed' }}
                                >
                                  Replace
                                </label>
                                <input
                                  id="priceEditProductImageReplace"
                                  type="file"
                                  accept="image/*"
                                  className="d-none"
                                  disabled={!canEdit}
                                  onChange={handleProductImageInputChange}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  disabled={!canEdit}
                                  onClick={() => {
                                    setProductImageRemoved(true)
                                    setProductImageFile(null)
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <label
                                className="btn btn-blue btn-sm mb-0"
                                htmlFor="priceEditProductImageAdd"
                                style={{ cursor: canEdit ? 'pointer' : 'not-allowed' }}
                              >
                                Add image
                              </label>
                              <input
                                id="priceEditProductImageAdd"
                                type="file"
                                accept="image/*"
                                className="d-none"
                                disabled={!canEdit}
                                onChange={handleProductImageInputChange}
                              />
                              {productImageRemoved && initialProductImage && (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm"
                                  disabled={!canEdit}
                                  onClick={() => setProductImageRemoved(false)}
                                >
                                  Undo remove
                                </button>
                              )}
                            </div>
                          )}
                          <small className="form-text text-muted d-block mt-1">
                            Optional. New images upload to{' '}
                            <code>Product_attachment/</code>. Remove clears the image on save.
                          </small>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Valid From
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="priceValidFrom"
                            className="form-control"
                            value={formData.priceValidFrom}
                            onChange={handleChange}
                            max={formData.priceValidTill || undefined}
                            disabled={!canEdit}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Valid Till
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="date"
                            name="priceValidTill"
                            className="form-control"
                            value={formData.priceValidTill}
                            onChange={handleChange}
                            min={formData.priceValidFrom || undefined}
                            disabled={!canEdit}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Remarks</label>
                        <div className="col-12 col-md-8">
                          <textarea
                            name="remarks"
                            className="form-control"
                            rows={3}
                            placeholder="Internal note for this price row"
                            value={formData.remarks}
                            onChange={handleChange}
                            disabled={!canEdit}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Status
                        </label>
                        <div className="col-12 col-md-8">
                          <select
                            name="status"
                            className="form-control"
                            value={formData.status}
                            onChange={handleChange}
                            disabled
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Existing Attachments
                        </label>
                        <div className="col-12 col-md-8">
                          {existingDownloadFiles.length === 0 ? (
                            <div>
                              <span className="text-muted">No download links loaded.</span>
                              <div className="mt-2">
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={loadDownloadLinks}
                                  disabled={isLoadingDownloads}
                                >
                                  {isLoadingDownloads ? 'Loading...' : 'Load download links'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {existingDownloadFiles.map((f, idx) => {
                                const isRemoved = Boolean(attachmentsToRemove[f.s3Key])
                                const replaceFile = replaceFilesByS3Key[f.s3Key]

                                return (
                                  <div
                                    key={`${f.s3Key}-${idx}`}
                                    className="d-flex align-items-center justify-content-between mb-2"
                                    style={{
                                      gap: '10px',
                                      opacity: isRemoved ? 0.5 : 1
                                    }}
                                  >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <a
                                        href={f.downloadUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                          display: 'inline-block',
                                          maxWidth: '100%',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}
                                      >
                                        {f.originalFileName || f.s3Key}
                                      </a>
                                      {replaceFile && (
                                        <div className="text-muted" style={{ fontSize: '12px' }}>
                                          Will replace with: {replaceFile.name}
                                        </div>
                                      )}
                                      {isRemoved && (
                                        <div className="text-muted" style={{ fontSize: '12px' }}>
                                          Marked for removal
                                        </div>
                                      )}
                                    </div>

                                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                      <label
                                        className="btn btn-outline-primary btn-sm mb-0"
                                        htmlFor={`replace-file-${idx}`}
                                        style={{ cursor: 'pointer' }}
                                        onMouseDown={(ev) => ev.preventDefault()}
                                      >
                                        Replace
                                      </label>
                                      <input
                                        id={`replace-file-${idx}`}
                                        type="file"
                                        className="d-none"
                                        disabled={!canEdit}
                                        onChange={(ev) => {
                                          const file = ev.target.files?.[0]
                                          ev.target.value = ''
                                          if (!file) return

                                          // If removed, unmark it because we're replacing
                                          setAttachmentsToRemove((prev) => {
                                            if (!prev[f.s3Key]) return prev
                                            const next = { ...prev }
                                            delete next[f.s3Key]
                                            return next
                                          })
                                          setReplaceFilesByS3Key((prev) => ({
                                            ...prev,
                                            [f.s3Key]: file
                                          }))
                                        }}
                                      />

                                      <button
                                        type="button"
                                        className={`btn btn-sm ${isRemoved ? 'btn-secondary' : 'btn-outline-danger'}`}
                                        disabled={!canEdit}
                                        onClick={() => {
                                          // toggles remove
                                          setReplaceFilesByS3Key((prev) => {
                                            if (!prev[f.s3Key]) return prev
                                            const next = { ...prev }
                                            delete next[f.s3Key]
                                            return next
                                          })
                                          setAttachmentsToRemove((prev) => {
                                            const next = { ...prev }
                                            if (next[f.s3Key]) {
                                              delete next[f.s3Key]
                                            } else {
                                              next[f.s3Key] = true
                                            }
                                            return next
                                          })
                                        }}
                                      >
                                        {isRemoved ? 'Undo' : 'Remove'}
                                      </button>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Add Attachments (optional)
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={addedFilesLabel}
                              readOnly
                            />
                            <div className="input-group-append">
                              <label
                                className="btn btn-blue mb-0"
                                htmlFor="priceAddedAttachments"
                                style={{ cursor: 'pointer' }}
                              >
                                Browse
                              </label>
                              <input
                                id="priceAddedAttachments"
                                type="file"
                                className="d-none"
                                multiple
                                disabled={!canEdit}
                                onChange={handleNewAttachmentsChange}
                              />
                            </div>
                          </div>
                          {addedFiles.length > 0 && (
                            <div className="mt-2">
                              <div className="text-muted" style={{ fontSize: '12px' }}>
                                New files to add:
                              </div>
                              <ul className="mb-0 pl-3" style={{ fontSize: '13px' }}>
                                {addedFiles.map((f, idx) => (
                                  <li key={`${f.name}-${f.size}-${idx}`}>
                                    {f.name}{' '}
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 ml-2"
                                      style={{ fontSize: '12px' }}
                                      onClick={() => {
                                        setAddedFiles((prev) =>
                                          prev.filter((_, i) => i !== idx)
                                        )
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isSubmitting || !canEdit}
                      >
                        {isSubmitting ? 'Updating...' : 'Update'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PriceTrackerEdit